<template>
  <div class="aroma-report-home">
    <header class="aroma-report-home__nav"><h1>睡眠报告</h1></header>
    <section class="aroma-report-home__greeting">
      <span>睡眠记录</span>
      <h2>{{ greeting }}</h2>
      <p>看看昨晚睡得怎么样</p>
    </section>
    <div class="aroma-report-home__section-tab">本次报告</div>

    <main class="aroma-report-home__content">
      <div v-if="initialLoading" class="aroma-report-home__skeleton">
        <van-loading size="26px">正在加载睡眠报告</van-loading>
      </div>
      <section v-else-if="pageError" class="aroma-report-home__state">
        <van-empty :description="pageError" />
        <van-button type="primary" size="small" @click="initPage">重新加载</van-button>
      </section>
      <section v-else-if="!latest && !history.length && !hasDevices" class="aroma-report-home__state is-unbound">
        <van-icon name="phone-o" />
        <h2>暂无睡眠报告</h2>
        <p>绑定设备后即可开始睡眠监测。</p>
        <van-button type="primary" @click="openScanner">绑定设备</van-button>
      </section>
      <section v-else-if="!latest" class="aroma-report-home__state">
        <van-icon name="description" />
        <h2>暂无睡眠报告</h2>
        <p>完成一晚睡眠监测后可查看。</p>
      </section>
      <template v-else>
        <aroma-home-report-card
          :date="latestDate"
          :metrics="metrics"
          :timeline="timeline"
          :detail-error="detailError"
          @retry="loadLatestDetail"
          @open="openReport(latest)"
        />
        <section class="aroma-report-home__history">
          <div class="aroma-report-home__history-title">
            <h2>历史睡眠记录</h2><span>共 {{ historyTotal }} 晚</span>
          </div>
          <van-list v-model="loadingMore" :finished="finished" :immediate-check="false" finished-text="没有更多了" @load="loadMore">
            <div v-if="history.length" class="aroma-report-home__history-list">
              <aroma-report-history-item v-for="item in history" :key="item.id" :item="item" :fallback-sn="activeSn" @select="openReport" />
            </div>
            <van-empty v-else description="暂无历史睡眠记录" image-size="72" />
          </van-list>
        </section>
      </template>
    </main>
    <aroma-tabbar />
  </div>
</template>

<script>
import API from '@/api'
import AromaHomeReportCard from '@/components/aroma-report/AromaHomeReportCard.vue'
import AromaReportHistoryItem from '@/components/aroma-report/AromaReportHistoryItem.vue'
import AromaTabbar from '@/components/aroma-report/AromaTabbar.vue'
import { resolveDeviceAction } from '@/utils/aroma-binding'
import { extractSleepStageSeries } from '@/utils/aroma-report-detail'
import { appendHistory, buildAccountReportDetailQuery, buildHomeMetrics, buildSleepStageTimeline, canRequestAccountReports, formatHomeReportDate, getGreeting, resolveReportDeviceSn, splitLatestReport } from '@/utils/aroma-report-home'
import { applySniCurrentResult, applySniLoginResult, extractSniProfile, getActiveSniSession, getSniAccountSession, getSniSession, normalizeSniSn, updateSniProfile } from '@/utils/sni-session'

const EMPTY_METRICS = { duration: '--', latency: '--', awakeCount: '--', efficiency: '--' }

export default {
  name: 'AromaReportList',
  components: { AromaHomeReportCard, AromaReportHistoryItem, AromaTabbar },
  data () {
    return {
      initialLoading: true,
      loadingMore: false,
      pageError: '',
      profile: {},
      latest: null,
      latestDetail: null,
      detailError: false,
      history: [],
      historyTotal: 0,
      pageNum: 1,
      pageSize: 20,
      finished: true,
      boundSession: null
    }
  },
  computed: {
    routeSn () { return normalizeSniSn(this.$route.query.sn) },
    accountSession () { return getSniAccountSession() },
    activeSn () { return resolveReportDeviceSn(this.routeSn, this.accountSession) },
    session () { return this.boundSession || getActiveSniSession(this.activeSn) },
    token () { return this.session && this.session.token },
    hasDevices () {
      return Array.isArray(this.accountSession && this.accountSession.deviceList) && this.accountSession.deviceList.length > 0
    },
    greeting () { return getGreeting(new Date(), this.profile.realName || (this.session && this.session.account && this.session.account.realName)) },
    latestDate () { return formatHomeReportDate(this.latest && this.latest.reportDate) },
    metrics () { return this.latestDetail ? buildHomeMetrics(this.latestDetail) : EMPTY_METRICS },
    timeline () { return this.latestDetail ? buildSleepStageTimeline(extractSleepStageSeries(this.latestDetail)) : null }
  },
  mounted () { this.initPage() },
  methods: {
    async initPage () {
      this.initialLoading = true
      this.pageError = ''
      this.latest = null
      this.latestDetail = null
      this.history = []
      this.historyTotal = 0
      try {
        if (!canRequestAccountReports(this.session)) {
          this.$router.replace({ name: 'AromaReportLogin', query: this.routeSn ? { sn: this.routeSn } : {} })
          return
        }
        if (this.routeSn && !getSniSession(this.routeSn) && this.accountSession) {
          const confirmed = await this.confirmHardwareDevice(this.routeSn)
          if (!confirmed) {
            await this.$router.replace({ name: 'AromaReportList' })
            await this.$nextTick()
            await this.initPage()
            return
          }
        }
        const current = await API.sniCurrent(this.token)
        applySniCurrentResult(current, this.token)
        this.profile = extractSniProfile(current)
        updateSniProfile(this.profile, this.activeSn)
        if (!this.profile.profileCompleted) {
          this.$router.replace({ name: 'AromaReportProfile', query: this.activeSn ? { sn: this.activeSn } : {} })
          return
        }
        await this.loadFirstPage()
      } catch (error) {
        this.pageError = error?.message || '报告加载失败，请稍后重试'
      } finally {
        this.initialLoading = false
      }
    },
    async confirmHardwareDevice (sn) {
      try {
        await this.$dialog.confirm({ title: '确认设备', message: `设备编号：${sn}`, confirmButtonText: '确认', cancelButtonText: '取消' })
      } catch (error) {
        return false
      }
      const action = resolveDeviceAction(this.accountSession, sn)
      const result = action === 'switch'
        ? await API.sniSwitchDevice({ sn }, this.accountSession.token)
        : await API.sniBindDevice({ sn }, this.accountSession.token)
      this.boundSession = applySniLoginResult(result)
      return true
    },
    async loadFirstPage () {
      const payload = { pageNum: 1, pageSize: this.pageSize }
      const page = await API.sniReportPageList(payload, this.token)
      const state = splitLatestReport(page)
      this.latest = state.latest
      this.history = state.history
      this.historyTotal = state.historyTotal
      this.pageNum = 2
      this.finished = page?.boolLastPage === true || (this.latest ? this.history.length + 1 : 0) >= Number(page?.total || 0)
      if (this.latest) await this.loadLatestDetail()
    },
    async loadLatestDetail () {
      if (!this.latest) return
      this.detailError = false
      try {
        this.latestDetail = await API.sniReportDetail(buildAccountReportDetailQuery(this.latest.id), this.token)
      } catch (error) {
        this.detailError = true
        this.latestDetail = null
      }
    },
    async loadMore () {
      if (this.finished) { this.loadingMore = false; return }
      try {
        const payload = { pageNum: this.pageNum, pageSize: this.pageSize }
        const page = await API.sniReportPageList(payload, this.token)
        const state = appendHistory({ latest: this.latest, history: this.history, historyTotal: this.historyTotal }, page)
        this.history = state.history
        this.pageNum += 1
        this.finished = page?.boolLastPage === true || this.history.length + 1 >= Number(page?.total || 0)
      } catch (error) {
        this.$toast(error?.message || error)
      } finally {
        this.loadingMore = false
      }
    },
    openScanner () { this.$router.push({ name: 'AromaDeviceScanner' }) },
    openReport (item) {
      const sn = normalizeSniSn(item && item.sn) || this.activeSn
      this.$router.push({ name: 'AromaReportDetail', query: { ...(sn ? { sn } : {}), reportId: item.id } })
    }
  }
}
</script>

<style scoped lang="scss">
.aroma-report-home { width: 100%; max-width: 520px; min-height: 100%; margin: 0 auto; padding-bottom: calc(76px + env(safe-area-inset-bottom)); overflow-x: hidden; background: #f5f6f8; box-sizing: border-box; }
.aroma-report-home__nav { display: flex; height: 58px; align-items: center; justify-content: center; padding: 0 16px; background: #fff; box-sizing: border-box; }
.aroma-report-home__nav h1 { margin: 0; color: #252d40; font-size: 16px; }
.aroma-report-home__greeting { position: relative; padding: 22px 20px 18px; }.aroma-report-home__greeting > span { color: #596afd; font-size: 13px; font-weight: 600; }.aroma-report-home__greeting h2 { margin: 8px 0 2px; color: #222a3d; font-size: 27px; line-height: 38px; }.aroma-report-home__greeting p { margin: 0; color: #9aa1af; font-size: 14px; }
.aroma-report-home__section-tab { margin: 0 20px 16px; padding: 14px; border-radius: 8px; color: #596afd; background: #fff; box-shadow: 0 5px 18px rgba(49,61,105,.05); font-size: 16px; font-weight: 600; text-align: center; }
.aroma-report-home__content { padding: 0 16px; }
.aroma-report-home__skeleton,.aroma-report-home__state { display: flex; min-height: 430px; align-items: center; justify-content: center; flex-direction: column; color: #8e95a3; text-align: center; }
.aroma-report-home__state > .van-icon { display: grid; width: 72px; height: 72px; place-items: center; border-radius: 24px; color: #596afd; background: #e9ecff; font-size: 34px; }
.aroma-report-home__state h2 { margin: 22px 0 8px; color: #262e42; font-size: 21px; }.aroma-report-home__state p { margin: 0 0 28px; font-size: 14px; }.aroma-report-home__state .van-button { min-width: 174px; height: 46px; border-color: #596afd; border-radius: 6px; background: #596afd; }
.aroma-report-home__history { margin-top: 28px; }.aroma-report-home__history-title { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }.aroma-report-home__history-title h2 { margin: 0; color: #252d40; font-size: 19px; }.aroma-report-home__history-title span { color: #939aa7; font-size: 13px; }.aroma-report-home__history-list { display: grid; gap: 12px; }
</style>
